import styled from '@emotion/styled';
import {
    Box,
    Card,
    StepConnector,
    stepConnectorClasses,
    StepLabel,
    stepLabelClasses
} from '@mui/material';
import { Center, Column } from '@dh/styles';
import { shouldForwardProps } from '@dh/utils';
import { Breakpoint, getBreakpointValue } from '@dh/types';

const StepperContainer = styled(Box, shouldForwardProps)<{ isExpanded: boolean }>`
    height: calc(
        100vh - 144px - 2 * 24px
    ); // View height - header height - 2 * padding (top and bottom)
    display: flex;
    flex-direction: ${({ isExpanded }) => (isExpanded ? 'row' : 'column')};
    padding-top: ${({ theme, isExpanded }) => (isExpanded ? theme.spacing(6) : theme.spacing(3))};

    .MuiMobileStepper-dot {
        margin: ${({ theme }) => `0 ${theme.spacing(1)}`};
    }
`;

interface ConnectorProps {
    breakpoint: Breakpoint;
    longerConnectorLines?: boolean;
}

const CustomisedLinearConnector = styled(
    StepConnector,
    shouldForwardProps
)<ConnectorProps>(({ breakpoint, longerConnectorLines }) => ({
    [`& .${stepConnectorClasses.line}`]: {
        height:
            breakpoint === Breakpoint.Expanded
                ? longerConnectorLines
                    ? '120px'
                    : '80px'
                : 'fit-content'
    }
}));

const CustomisedUnorderedConnector = styled(
    StepConnector,
    shouldForwardProps
)<ConnectorProps>(({ breakpoint, theme }) => ({
    [`& .${stepConnectorClasses.line}`]: {
        height: breakpoint === Breakpoint.Expanded ? '40px' : 'fit-content',
        margin: theme.spacing(1)
    }
}));

const StepperDot = styled(Center, shouldForwardProps)<{ isActive: boolean }>`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${({ theme, isActive }) =>
        isActive ? theme.colors.darkSurfaceTint : theme.colors.primary};
`;

const StepperLabel = styled(StepLabel)(({ theme }) => ({
    [`& .${stepLabelClasses.iconContainer}`]: {
        color: theme.colors.outline
    },
    [`& .${stepLabelClasses.label}`]: {
        [`&.${stepLabelClasses.active}`]: {
            color: theme.colors.onBackground
        },
        color: theme.colors.onSurfaceVariant,
        cursor: 'pointer'
    }
}));

const LinearStepperLabel = styled(StepperLabel)(({ theme }) => ({
    [`& .${stepLabelClasses.label}`]: {
        [`&.${stepLabelClasses.completed}`]: {
            color: theme.colors.onBackground
        },
        cursor: 'default'
    },
    [`& .${stepLabelClasses.root}`]: {
        [`&.${stepLabelClasses.active}`]: {
            padding: '0px'
        },
        padding: '0px'
    }
}));

const StepperContent = styled(Card, shouldForwardProps)<{ isMedium: boolean }>`
    width: auto;
    min-width: 50%;
    max-width: 636px;
    flex: 1;
    align-self: center;
    justify-content: space-between;
    align-items: center;
    border-radius: 4px;
    border: ${({ theme }) => `1px solid ${theme.colors.primary}`};
    margin-right: ${({ theme, isMedium }) => (isMedium ? theme.spacing(2) : theme.spacing(0))};
`;

const StyledForm = styled('form')<{ isExpanded: boolean }>`
    width: 100%;
    max-width: 636px;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    height: 100%;
    padding-top: ${({ theme, isExpanded }) => theme.spacing(isExpanded ? 5 : 2)};
    padding-bottom: ${({ theme, isExpanded }) => theme.spacing(isExpanded ? 5 : 2)};
    padding-left: ${({ theme, isExpanded }) => theme.spacing(isExpanded ? 7 : 3)};
    padding-right: ${({ theme, isExpanded }) => theme.spacing(isExpanded ? 7 : 3)};
`;

const StepperButtonRow = styled(Box, shouldForwardProps)<{ hasBorder: boolean }>`
    display: flex;
    flex-direction: row;
    width: 100%;
    max-width: 636px;
    height: fit-content;
    gap: ${({ theme }) => theme.spacing(2)};
    flex-shrink: 0;
    border-top: ${({ theme, hasBorder }) =>
        `1px solid ${hasBorder ? theme.colors.primary : 'transparent'}`};
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(7)}`};
`;

const UnorderedStepperButtonRow = styled(Box, shouldForwardProps)<{
    hasBorder: boolean;
    isLastStep: boolean;
    breakpoint: Breakpoint;
}>`
    display: grid;
    flex-direction: row;
    grid-template-columns: 1fr 1fr 1fr;
    width: 100%;
    max-width: 636px;
    height: fit-content;
    gap: ${({ theme }) => theme.spacing(2)};
    flex-shrink: 0;
    border-top: ${({ theme, hasBorder }) =>
        `1px solid ${hasBorder ? theme.colors.primary : 'transparent'}`};
    padding: ${({ theme, breakpoint }) =>
        `${theme.spacing(2)} ${theme.spacing(getBreakpointValue(breakpoint, 4, 5, 7))}`};
    justify-content: ${({ isLastStep }) => (isLastStep ? 'flex-start' : 'flex-end')};
`;

const StepperBox = styled(Column, shouldForwardProps)`
    gap: ${({ theme }) => theme.spacing(2)};
    padding-bottom: ${({ theme }) => theme.spacing(2)};
`;

export {
    StepperContainer,
    StepperLabel,
    LinearStepperLabel,
    StepperContent,
    StepperBox,
    StyledForm,
    StepperButtonRow,
    UnorderedStepperButtonRow,
    StepperDot,
    CustomisedLinearConnector,
    CustomisedUnorderedConnector
};
